import { DaySlots } from '@fingertip/creator-proto/gen/fingertip/creator/event_type/v1/event_type_pb'
import {
  addMinutes,
  eachDayOfInterval,
  endOfWeek,
  format,
  startOfWeek,
} from 'date-fns'
import { useMemo } from 'react'

export const getWeekDays = (style: 'long' | 'short' = 'long'): string[] => {
  const formatString = style === 'short' ? 'EEE' : 'EEEE'
  const now = new Date()
  const weekDays: string[] = []
  const start = startOfWeek(now)
  const end = endOfWeek(now)
  eachDayOfInterval({ start, end }).forEach((day) => {
    weekDays.push(format(day, formatString))
  })

  return weekDays
}

export type WeekdayFormat = 'long' | 'short'

export function nameOfDay(date: Date, style: 'long' | 'short' = 'long') {
  const formatString = style === 'long' ? 'EEEE' : 'EEE'
  return format(date, formatString)
}

export const defaultDayRange: TimeRange = {
  start: '09:00',
  end: '17:00',
}

export type TimeRange = {
  userId?: number | null
  start: string
  end: string
}

export const yyyymmdd = (date: Date) => format(date, 'yyyy-MM-dd')

export const getNonEmptyScheduleDays = (slots?: {
  [key: string]: DaySlots
}) => {
  if (typeof slots === 'undefined') return []
  return Object.keys(slots).filter((day) => slots[day].slots.length > 0)
}

export const useNonEmptyScheduleDays = (slots?: {
  [key: string]: DaySlots
}) => {
  const days = useMemo(() => getNonEmptyScheduleDays(slots), [slots])

  return days
}

export const useSlotsForAvailableDates = (
  dates: (Date | null)[],
  slots?: {
    [key: string]: DaySlots
  },
) => {
  const slotsForDates = useMemo(() => {
    if (slots === undefined) return []
    return dates
      .filter((date) => date !== null)
      .map((date) => {
        const dateStr = format(date || new Date(), 'yyyy-MM-dd')
        return {
          slots: slots[`${dateStr}`]?.slots || [],
          date,
        }
      })
  }, [dates, slots])
  return slotsForDates
}

export const timeToDate = (str: string) => {
  const [hours, minutes] = str.split(':')
  const date = new Date()
  date.setMilliseconds(0)
  date.setSeconds(0)
  date.setMinutes(Number(minutes))
  date.setHours(Number(hours))
  return date
}

export const dateToTime = (date: Date): string => {
  return `${date.getHours().toString().padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')}`
}

export const newEndTime = (newStartTime: string): string => {
  const newStartDate = timeToDate(newStartTime)

  const newEndTime = addMinutes(newStartDate, 30)

  return dateToTime(newEndTime)
}

export const formatCentsToDollars = (cents: number) => {
  return (cents / 100).toFixed(2)
}

export const parseDollarToCents = (dollarString: string | undefined) => {
  if (!dollarString) {
    return 0
  }

  // Remove non-numeric and non-decimal characters
  const sanitizedString = dollarString.replace(/[^0-9.]/g, '')
  const floatNumber = parseFloat(sanitizedString)
  return Math.round(floatNumber * 100)
}
